import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, Info, Loader2, Lock, RotateCcw } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useAdminReengage, useClientOnboardingNurture, useSpecialists } from '../../../services/clientOnboarding';
import ReengagePicker, { reengageReady } from '../workspace/ReengagePicker';
import { VIEW_ONLY_TITLE } from '../onboardingSupport/ownership';

// Stalled (Nic 2026-10-10): built to match the Sales board's Nurture tab.
// Same columns under one header, the reason as the readable headline, filter
// chips by why they're here (server-side, with counts), Mine / Everyone, and
// paging at the top right. Stalled clients are left alone: the moment we
// detect them calling or texting, they go straight back to a specialist.
// Anyone who works the page can bring one back by hand, with a reason that
// lands on their history (Nic 2026-10-12, like Sales; was admins only). A
// row opens their Workspace, read only until they're back.

// How long they've been quiet, counted from today (the server's
// STALLED_CATEGORIES). Nothing names a one-time event, so the labels make
// sense years from now (Nic 2026-10-10).
const CATEGORIES = [
  { key: 'under_3m', label: 'Quiet under 3 months', dot: 'bg-amber-400' },
  { key: '3_to_6m', label: 'Quiet 3 to 6 months', dot: 'bg-orange-400' },
  { key: 'over_6m', label: 'Quiet 6+ months', dot: 'bg-rose-400' },
];
const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

// The headline: when we last heard from them. Every stall is automatic.
const reasonOf = (r) => ({
  text: r.lastActivityAt
    ? `No contact since ${moment(r.lastActivityAt).format(
        moment(r.lastActivityAt).year() === moment().year() ? 'MMM D' : 'MMM D, YYYY',
      )}`
    : 'No contact on record',
  tag: 'Auto',
});

const ago = (at) => (at ? moment(at).fromNow() : null);
const initialsOf = (name) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('') || '?';

// lg+ is a table; below it each row folds into a small card.
const GRID =
  'lg:grid lg:grid-cols-[minmax(0,1.25fr)_6rem_minmax(0,1.8fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_5rem_7rem] lg:items-center lg:gap-x-6';
// Pixel padding on purpose: px-4 / px-5 are also Bootstrap classes.
const PAD_X = 'px-[16px] lg:px-[20px]';

const Chip = ({ active, onClick, dot, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold outline-none focus:outline-none',
      active
        ? 'bg-slate-900 text-white dark:!bg-slate-100 dark:!text-slate-900'
        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10',
    )}
  >
    {dot ? <span className={cn('size-1.5 rounded-full', dot)} /> : null}
    {label}
    <span className={cn('tabular-nums', active ? 'opacity-70' : 'text-slate-400 dark:text-slate-500')}>
      {count}
    </span>
  </button>
);

// Bringing a client back by hand. The reason is required; the Onboarding
// Manager also picks who gets them (Nic 2026-10-23).
const ReengageForm = ({ row, viewer = null, onDone }) => {
  const [reason, setReason] = useState('');
  const [toUserId, setToUserId] = useState('');
  const reengage = useAdminReengage();
  const specialists = useSpecialists(!!viewer?.isManager);
  const ready = !!reason.trim() && reengageReady(viewer, toUserId);
  const go = async () => {
    if (!ready) return;
    try {
      const res = await reengage.mutateAsync({
        onboardingId: row.onboardingId,
        reason: reason.trim(),
        ...(viewer?.isManager ? { toUserId } : {}),
      });
      toast.success(
        `${row.name || 'They'} ${res?.ownerName ? `went to ${res.ownerName}` : 'are back on the board'}.`,
      );
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.msg || "They couldn't be brought back.");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pl-11 lg:col-span-full lg:pl-11">
      <input
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={reason}
        maxLength={200}
        onChange={(e) => setReason(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') go();
          if (e.key === 'Escape') onDone();
        }}
        placeholder="Why? e.g. called from his wife's phone"
        className="h-8 min-w-0 flex-1 rounded-lg bg-white px-2.5 text-xs text-slate-700 outline-none ring-1 ring-slate-200 focus:ring-slate-300 sm:max-w-sm dark:!bg-white/5 dark:text-slate-100 dark:ring-white/10"
      />
      <ReengagePicker
        viewer={viewer}
        noun="specialist"
        value={toUserId}
        onChange={setToUserId}
        options={(specialists.data?.specialists || []).map((sp) => ({ value: sp.userId, label: sp.name }))}
      />
      <button
        type="button"
        disabled={!ready || reengage.isLoading}
        onClick={go}
        className="inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-lg bg-slate-900 px-3 text-xs font-semibold text-white outline-none hover:bg-slate-700 focus:outline-none disabled:opacity-40 dark:!bg-slate-100 dark:!text-slate-900"
      >
        {reengage.isLoading ? <Loader2 size={12} className="animate-spin" /> : null}
        Bring back
      </button>
      <button
        type="button"
        onClick={onDone}
        className="h-8 whitespace-nowrap rounded-lg px-2 text-xs font-semibold text-slate-500 outline-none hover:bg-slate-100 focus:outline-none dark:text-slate-400 dark:hover:!bg-white/10"
      >
        Cancel
      </button>
    </div>
  );
};

const Row = ({ r, canReengage, viewer = null, onOpen }) => {
  const [bringing, setBringing] = useState(false);
  const reason = reasonOf(r);
  const cat = CATEGORY_BY_KEY[r.category];
  const stalled = r.nurturedAt ? moment(r.nurturedAt) : null;
  return (
    // The row opens their Workspace, read only until they're brought back
    // (Nic 2026-10-12). The Re-engage button and its form don't.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <li
      tabIndex={0}
      onClick={() => !bringing && onOpen?.(r.onboardingId)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !bringing && e.target === e.currentTarget) onOpen?.(r.onboardingId);
      }}
      className={cn(
        'group flex cursor-pointer flex-col gap-2 py-3 outline-none hover:bg-slate-50/80 focus-visible:bg-sky-50 dark:hover:!bg-white/[0.03] dark:focus-visible:!bg-sky-500/10',
        PAD_X,
        GRID,
        bringing && 'lg:gap-y-3',
      )}
    >
      {/* Client */}
      <div className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-600 dark:!bg-white/10 dark:text-slate-300"
        >
          {initialsOf(r.name)}
        </span>
        <div className="min-w-0 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
          {r.name || 'Unnamed client'}
        </div>
      </div>

      {/* Times brought back */}
      <div className="hidden justify-center lg:flex">
        {r.reengagedCount > 0 ? (
          <span
            title={`Brought back from Stalled ${r.reengagedCount} time${r.reengagedCount === 1 ? '' : 's'}`}
            className={cn(
              'inline-flex h-6 items-center gap-1 rounded-full px-2 text-xs font-semibold tabular-nums',
              r.reengagedCount >= 3
                ? 'bg-amber-100 text-amber-800 dark:!bg-amber-400/15 dark:text-amber-300'
                : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
            )}
          >
            <RotateCcw size={11} /> {r.reengagedCount}
          </span>
        ) : (
          <span className="text-[13px] text-slate-300 dark:text-slate-600">-</span>
        )}
      </div>

      {/* Why they're here */}
      <div className="min-w-0 pl-11 lg:pl-0">
        <div
          className="line-clamp-2 text-[13px] leading-snug text-slate-700 dark:text-slate-200"
          title={reason.text}
        >
          {reason.text}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500">
          {cat ? <span className={cn('size-1.5 shrink-0 rounded-full', cat.dot)} /> : null}
          <span>{cat?.label || 'Stalled'}</span>
          <span aria-hidden>·</span>
          <span>{reason.tag}</span>
        </div>
      </div>

      {/* Stalled: when. On small screens the specialist rides along here. */}
      <div className="flex min-w-0 items-baseline gap-2 pl-11 lg:block lg:pl-0">
        <div className="whitespace-nowrap text-[13px] text-slate-700 dark:text-slate-200">
          {stalled ? stalled.format('MMM D') : '-'}
          {stalled ? (
            <span className="text-slate-400 dark:text-slate-500"> · {stalled.fromNow()}</span>
          ) : null}
        </div>
        <div className="truncate text-[11px] text-slate-400 dark:text-slate-500 lg:hidden">
          {r.isMine ? 'Your client' : r.specialistName ? `${r.specialistName}'s client` : 'No specialist'}
        </div>
      </div>

      {/* Specialist: who onboarded them. */}
      <div className="hidden min-w-0 items-center gap-2 lg:flex">
        {r.specialistName ? (
          <>
            <span
              aria-hidden
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                r.isMine
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
              )}
            >
              {initialsOf(r.specialistName)}
            </span>
            <span className="truncate text-[13px] text-slate-700 dark:text-slate-200" title={r.specialistName}>
              {r.isMine ? 'You' : r.specialistName}
            </span>
          </>
        ) : (
          <span className="text-[13px] italic text-slate-400 dark:text-slate-500">No specialist</span>
        )}
      </div>

      {/* Last activity */}
      <div className="hidden min-w-0 lg:block">
        {r.lastActivityAt ? (
          <div
            className="truncate text-[13px] text-slate-600 dark:text-slate-300"
            title={moment(r.lastActivityAt).format('ddd MMM D, h:mm A')}
          >
            {ago(r.lastActivityAt)}
          </div>
        ) : (
          <div className="text-[13px] italic text-slate-400 dark:text-slate-500">Never</div>
        )}
      </div>

      {/* Checklist */}
      <div className="hidden text-right text-[13px] font-semibold tabular-nums text-slate-500 dark:text-slate-400 lg:block">
        {r.progress.done}/{r.progress.total}
      </div>

      {/* One fixed slot so the column lines up. */}
      <div className="flex pl-11 lg:justify-end lg:pl-0">
        {/* Admins and viewers see it greyed (Nic 2026-10-23). */}
        {(canReengage || viewer?.canAct === false) && !bringing ? (
          <button
            type="button"
            disabled={!canReengage}
            onClick={(e) => {
              e.stopPropagation();
              setBringing(true);
            }}
            title={canReengage ? 'Bring them back by hand, with a reason' : VIEW_ONLY_TITLE}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-[1px] px-3 text-xs font-semibold outline-none focus:outline-none disabled:pointer-events-none disabled:opacity-50',
              'border-slate-200 !bg-white !text-slate-700 hover:!border-slate-900 hover:!bg-slate-900 hover:!text-white group-hover:!border-slate-900 group-hover:!bg-slate-900 group-hover:!text-white',
              'dark:!border-white/15 dark:!bg-transparent dark:!text-slate-200 dark:hover:!border-slate-100 dark:hover:!bg-slate-100 dark:hover:!text-slate-900 dark:group-hover:!border-slate-100 dark:group-hover:!bg-slate-100 dark:group-hover:!text-slate-900',
            )}
          >
            <RotateCcw size={13} /> Re-engage
          </button>
        ) : null}
      </div>

      {bringing ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={(e) => e.stopPropagation()} className="contents">
          <ReengageForm row={r} viewer={viewer} onDone={() => setBringing(false)} />
        </div>
      ) : null}
    </li>
  );
};

// q is the page's one search (the header box), already debounced.
const StalledView = ({ q = '', canReengage = false, viewer = null, onOpen = null }) => {
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState('');
  const [mine, setMine] = useState(false);
  const list = useClientOnboardingNurture({ page, q, reason, mine });

  // A new search or filter starts at page 1.
  useEffect(() => setPage(1), [q, reason, mine]);

  const rows = list.data?.rows || [];
  const total = list.data?.total || 0;
  const counts = list.data?.counts || {};
  const pageSize = list.data?.pageSize || 50;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);

  // Chips with nobody in them stay out of the way, unless picked.
  const chips = CATEGORIES.filter((c) => counts[c.key] > 0 || reason === c.key);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Filters */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="os-scroll -mx-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-1 py-0.5">
          <Chip active={!reason} onClick={() => setReason('')} label="All" count={counts.all ?? total} />
          {chips.map((c) => (
            <Chip
              key={c.key}
              active={reason === c.key}
              onClick={() => setReason(reason === c.key ? '' : c.key)}
              label={c.label}
              count={counts[c.key] || 0}
              dot={c.dot}
            />
          ))}
        </div>
        <div
          role="group"
          aria-label="Whose clients"
          className="flex shrink-0 items-center rounded-full bg-slate-200/70 p-0.5 dark:!bg-white/10"
        >
          {[
            { v: false, label: 'Everyone' },
            { v: true, label: 'Mine' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              aria-pressed={mine === o.v}
              onClick={() => setMine(o.v)}
              className={cn(
                'h-6 rounded-full px-3 text-xs font-semibold outline-none focus:outline-none',
                mine === o.v
                  ? 'bg-white text-slate-800 shadow-sm dark:!bg-slate-100 dark:!text-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* The rule, and paging */}
      <div className="mb-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Info size={13} className="shrink-0 text-slate-400" />
          Stalled clients are left alone. The moment they call or text, they go straight back to a specialist.
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Lock size={12} className="shrink-0 text-slate-400" />
          Contact details stay hidden while they&apos;re stalled.
        </span>
        <div className="ml-auto flex items-center gap-1">
          <span className="whitespace-nowrap tabular-nums">
            {total ? (
              <>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {from}-{to}
                </span>{' '}
                of {total}
              </>
            ) : (
              '0'
            )}
          </span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            aria-label="Next page"
            className="rounded-lg p-1 text-slate-500 outline-none hover:bg-slate-200/60 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="os-scroll relative min-h-0 flex-1 overflow-y-auto rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
        {/* Column header, lg+ only (below it rows are cards). */}
        <div
          className={cn(
            'sticky top-0 z-10 hidden border-b-[1px] border-slate-100 bg-white/95 py-2 text-[10.5px] font-bold uppercase tracking-wider text-slate-400 backdrop-blur dark:!border-white/10 dark:!bg-surface-card/95 dark:text-slate-500 lg:grid',
            PAD_X,
            GRID,
          )}
        >
          <div>Client</div>
          <div className="text-center">Re-engaged</div>
          <div>Why they&apos;re here</div>
          <div>Stalled</div>
          <div>Specialist</div>
          <div>Last activity</div>
          <div className="text-right">Checklist</div>
          <div className="text-right">
            {list.isFetching && !list.isLoading ? <Loader2 size={12} className="ml-auto animate-spin" /> : null}
          </div>
        </div>

        {list.isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 size={20} className="animate-spin text-slate-400" />
          </div>
        ) : list.isError ? (
          <div className="flex h-40 items-center justify-center text-sm font-semibold text-rose-500">
            {list.error?.response?.data?.msg || "Stalled couldn't be loaded."}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-1 text-sm text-slate-400">
            <div className="font-semibold text-slate-500 dark:text-slate-400">
              {q || reason || mine ? 'Nobody matches.' : 'Nobody is stalled.'}
            </div>
            {reason || mine ? (
              <button
                type="button"
                onClick={() => {
                  setReason('');
                  setMine(false);
                }}
                className="text-xs font-semibold text-sky-600 outline-none hover:underline focus:outline-none dark:text-sky-400"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          <ul
            aria-label="Stalled"
            className={cn(
              'mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]',
              list.isFetching && !list.isLoading && 'opacity-70',
            )}
          >
            {rows.map((r) => (
              <Row key={r.onboardingId} r={r} canReengage={canReengage} viewer={viewer} onOpen={onOpen} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default StalledView;
